import React, { useEffect, useState } from "react";
import {
    Button,
    TextField,
} from "@mui/material";
import Grid2 from "@mui/material/Unstable_Grid2";
import { apiCall } from "../../../services/apiClient";
import { RootState, useAppDispatch, useAppSelector } from "../../../framework/customStore";
import { fetchEnd, fetchStart, showErrorMessage, showSuccessMessage } from "../../../models/store/storeActions";
import { showApiError } from "../../framework/formUtils";
import { fetchTransportOrderInvoice, selectEditEnabled } from "../../../store/transportOrderInvoiceSlice";
import { ICustomerDetails } from "../../../models/customer/customerDetails";
import { InvoiceUpdateDto } from "../../../models/transportOrderInvoice/invoiceUpdateDto";
import { CustomerDetails } from "./customerDetails";
import { TransportOrderInvoiceDetails } from "./TransportOrderInvoiceDetails";
import { CustomerSelect } from "../components/customerSelect";
import { useTranslation } from "react-i18next";

interface ITransportOrderInvoiceEditProps {
    id: string;
    disableEdit?: boolean;
}

export const TransportOrderInvoiceEdit = ({ id, disableEdit }: ITransportOrderInvoiceEditProps) => {
    const { t } = useTranslation();
    const invoiceDetails = useAppSelector((state: RootState) => state.transportOrderInvoice.selectedInvoice);
    const [reference, setReference] = useState(invoiceDetails?.reference || "");
    const [message, setMessage] = useState(invoiceDetails?.invoiceMessage || "");
    const [customerDetails, setCustomerDetails] = useState<ICustomerDetails>();
    const [invoiceCustomer, setInvoiceCustomer] = useState("");
    const dispatch = useAppDispatch();
    const handleReferenceChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setReference(event.target.value);
    };
    const handleMessageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setMessage(event.target.value);
    };
    const updatedInvoiceData: InvoiceUpdateDto = {
        customerId: invoiceCustomer,
        reference: reference,
        invoiceMessage: message,
    };
    const disableButton = invoiceDetails?.invoiceMessage === message &&
        invoiceDetails?.reference === reference &&
        !invoiceCustomer;
    // Use Redux state for editEnabled unless overridden by the disableEdit prop.
    const isEditEnabled = useAppSelector(selectEditEnabled);
    const editEnabled = disableEdit ? false : isEditEnabled;

    useEffect(() => {
        if (invoiceDetails) setInvoiceCustomer(invoiceDetails.customerId);
    }, [invoiceDetails]);

    const handleUpdateInvoice = () => {
        dispatch(fetchStart());
        apiCall<InvoiceUpdateDto>(`Invoices/${id}`, "PUT", updatedInvoiceData)
            .then((response) => {
                if (response.status === 204 || response.status === 200) {
                    dispatch(showSuccessMessage(t("transport.invoicing.invoiceInformationSaveSuccess")));
                    dispatch(fetchTransportOrderInvoice(id) as any);
                } else {
                    dispatch(showErrorMessage(t("transport.invoicing.invoiceInformationSaveFailed")));
                }
            })
            .catch((error) => {
                showApiError(error.message);
            })
            .finally(() => {
                dispatch(fetchEnd());
            });
    };

    const handleCustomerDetailsCall = (customerDetails: ICustomerDetails) => setCustomerDetails(customerDetails);

    return (
        <div style={{ marginLeft: 32, marginRight: 32 }}>
            <Grid2 container py={2} spacing={7}>
                <Grid2 container xs={5} minWidth={500}>
                    <Grid2 xs={4}>
                        <TextField
                            value={invoiceDetails?.invoiceNumber || ""}
                            label={t("transport.invoicing.invoiceNumber")}
                            fullWidth
                            disabled
                        />
                    </Grid2>
                    <Grid2 xs={8}>
                        <CustomerSelect
                            value={invoiceCustomer}
                            onChange={(val: string) => setInvoiceCustomer(val)}
                            selectedLabel={customerDetails?.name || invoiceDetails?.customerName}
                            required
                            fullWidth
                            disabled={!editEnabled || invoiceDetails?.transportOrderRows?.length > 0}
                        />
                    </Grid2>
                    <Grid2 xs={12}>
                        <TextField
                            value={reference}
                            onChange={handleReferenceChange}
                            label={t("transport.invoicing.referenceInformation")}
                            fullWidth
                            inputProps={{
                                readOnly: !editEnabled
                            }}
                            disabled={!editEnabled}
                        />
                    </Grid2>
                </Grid2>

                <Grid2 container xs={5} minWidth={500}>
                    <Grid2 xs={12}>
                        <TextField
                            value={message}
                            onChange={handleMessageChange}
                            label={t("common.message")}
                            fullWidth
                            multiline rows={6}
                            inputProps={{
                                readOnly: !editEnabled
                            }}
                            disabled={!editEnabled}
                        />
                    </Grid2>
                </Grid2>

                {editEnabled &&
                <Grid2 xs={12} style={{ display: "flex" }}>
                    <Button
                        variant="contained"
                        onClick={() => void handleUpdateInvoice()}
                        disabled={disableButton}
                    >
                        {t("transport.invoicing.saveInvoiceInformation")}
                    </Button>
                </Grid2>}

                <Grid2 xs={12} pt={0} mb={2}>
                    <CustomerDetails
                        customerId={invoiceDetails?.customerId}
                        handleCustomerDetailsCall={handleCustomerDetailsCall}
                    />
                </Grid2>
            </Grid2>

            <TransportOrderInvoiceDetails editEnabled={editEnabled} />
        </div>
    );
};
